import cn from 'classnames';
import { FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { PartnerExportWarning } from '@/shared/api/transcriptor';

import { i18n, type PartnerExportWarningKind } from '../config';

import styles from './PartnerExportWarnings.module.scss';

type Props = {
  warnings: PartnerExportWarning[];
  partnerName: string;
  className?: string;
  // The note's own words for a line, to say where a warning points.
  describeLine?: (lineId: string) => string;
  onShowLine?: (lineId: string) => void;
};

const isKnownKind = (
  kind: string,
): kind is PartnerExportWarningKind | 'other' =>
  Object.hasOwn(i18n.partnerExportWarningMessages, kind);

// Where the partner's text cannot carry the note exactly, next to its copy:
// folded to a count, listed on request, each place leading to its line. The
// copy never waits for them.
export const PartnerExportWarnings: FC<Props> = ({
  warnings,
  partnerName,
  className,
  describeLine,
  onShowLine,
}) => {
  const { formatMessage } = useIntl();
  const [open, setOpen] = useState(false);

  if (!warnings.length) {
    return null;
  }
  return (
    <div
      className={cn(styles.warnings, className)}
      data-component="workspace-note-partner-warnings"
    >
      <button
        type="button"
        className={cn('p4', styles.toggle)}
        aria-expanded={open}
        onClick={() => setOpen((shown) => !shown)}
      >
        <FormattedMessage
          id="workspace.note.partnerWarnings"
          defaultMessage="Not copied exactly to {partner}: {count, plural, one {# place} other {# places}}"
          values={{ partner: partnerName, count: warnings.length }}
        />
      </button>
      {open && (
        <ul className={styles.list}>
          {warnings.map((warning, index) => {
            const { kind, lineId } = warning;
            const what = formatMessage(
              i18n.partnerExportWarningMessages[
                isKnownKind(kind) ? kind : 'other'
              ],
            );
            const where = lineId ? describeLine?.(lineId) : '';
            const text = where ? `${what}: ${where}` : what;
            return (
              <li
                key={`${kind}:${lineId}:${warning.blockId}:${index}`}
                className="p4"
              >
                {lineId && onShowLine ? (
                  <button
                    type="button"
                    className={cn('p4', styles.link)}
                    onClick={() => onShowLine(lineId)}
                  >
                    {text}
                  </button>
                ) : (
                  text
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
